import type { PlayerSnapshot } from "../shared/protocol";
import { PLANE, SKILLS, LASER } from "../shared/protocol";

export class HUD {
  private hud = document.getElementById("hud")!;
  private hpFill = document.getElementById("hpFill")!;
  private spdFill = document.getElementById("spdFill")!;
  private thrFill = document.getElementById("thrFill")!;
  private rpmFill = document.getElementById("rpmFill")!;
  private hpReadout = document.getElementById("hpReadout")!;
  private spdReadout = document.getElementById("spdReadout")!;
  private thrReadout = document.getElementById("thrReadout")!;
  private skill1Cd = document.querySelector("#skill-1 .cd") as HTMLElement;
  private skill2Cd = document.querySelector("#skill-2 .cd") as HTMLElement;
  private skill3Cd = document.querySelector("#skill-3 .cd") as HTMLElement;
  private scoreboard = document.getElementById("scoreboard")!;
  private netStat = document.getElementById("netStat")!;
  private netState = document.getElementById("netState")!;
  private killfeed = document.getElementById("killfeed")!;
  private deadOverlay = document.getElementById("deadOverlay")!;
  private respawnIn = document.getElementById("respawnIn")!;
  private respawnBtn = document.getElementById("respawnBtn") as HTMLButtonElement;
  private laserStatus = document.getElementById("laserStatus")!;
  private laserBarFill = document.getElementById("laserBarFill")!;
  private laserText = document.getElementById("laserText")!;

  constructor(private onRespawn: () => void) {
    this.respawnBtn.addEventListener("click", () => this.onRespawn());
  }

  show() { this.hud.classList.remove("hidden"); }
  hide() { this.hud.classList.add("hidden"); }

  updateLocal(me: PlayerSnapshot | null) {
    if (!me) {
      this.hpFill.style.width = "0%";
      this.spdFill.style.width = "0%";
      this.deadOverlay.classList.add("hidden");
      return;
    }
    this.hpFill.style.width = Math.max(0, Math.min(100, (me.hp / PLANE.hp) * 100)) + "%";
    this.hpReadout.textContent = Math.max(0, Math.round(me.hp)).toString();
    const speed = Math.hypot(me.vx, me.vy, me.vz);
    this.spdFill.style.width = Math.max(0, Math.min(100, (speed / (PLANE.maxSpeed + SKILLS.dash.boost)) * 100)) + "%";
    this.spdReadout.textContent = Math.round(speed).toString();
    // Throttle bar shows commanded throttle (light cyan) and current engine RPM (bright cyan).
    this.thrFill.style.width = Math.round(Math.max(0, Math.min(1, me.throttle)) * 100) + "%";
    this.rpmFill.style.width = Math.round(Math.max(0, Math.min(1, me.engineRpm)) * 100) + "%";
    this.thrReadout.textContent = Math.round(me.throttle * 100) + "%";
    this.applyCd(this.skill1Cd, me.dashCd, SKILLS.dash.cooldown);
    this.applyCd(this.skill2Cd, me.missileCd, SKILLS.missile.cooldown);
    this.applyCd(this.skill3Cd, me.shieldCd, SKILLS.shield.cooldown);

    if (!me.alive) {
      this.deadOverlay.classList.remove("hidden");
    } else {
      this.deadOverlay.classList.add("hidden");
    }

    // ===== Laser status =====
    // - READY:    full bar, "READY · SPACE", pulsing red
    // - CHARGING: bar fills 0 → 100% (mana-style buildup), bright pulsing
    // - COOLDOWN: bar depletes 100% → 0%, dimmed gray (so it's visually
    //             distinct from "ready/charging" — the bar empties as you
    //             wait, hits 0 when ready again)
    this.laserStatus.classList.remove("charging", "cooldown", "ready");
    if (me.laserChargeT > 0) {
      this.laserStatus.classList.add("charging");
      const pct = Math.max(0, Math.min(1, 1 - me.laserChargeT / (me.laserChargeMax || LASER.charge)));
      this.laserBarFill.style.width = (pct * 100).toFixed(0) + "%";
      this.laserText.textContent = `CHARGING ${me.laserChargeT.toFixed(1)}s`;
    } else if (me.laserCd > 0) {
      this.laserStatus.classList.add("cooldown");
      const pct = Math.min(1, me.laserCd / LASER.cooldown);
      this.laserBarFill.style.width = (pct * 100).toFixed(0) + "%";
      this.laserText.textContent = `COOLDOWN ${me.laserCd.toFixed(1)}s`;
    } else {
      this.laserStatus.classList.add("ready");
      this.laserBarFill.style.width = "100%";
      this.laserText.textContent = "READY · SPACE";
    }
  }

  setRespawnCountdown(s: number) {
    this.respawnIn.textContent = s.toFixed(1);
  }

  private applyCd(el: HTMLElement, cd: number, total: number) {
    const pct = total > 0 ? Math.min(1, Math.max(0, cd / total)) : 0;
    el.style.transform = `scaleY(${pct})`;
  }

  setPing(ms: number) {
    this.netStat.textContent = `${Math.round(ms)} ms`;
  }
  setNetStatus(status: "connecting" | "open" | "reconnecting" | "closed") {
    this.netState.classList.remove("net-open", "net-connecting", "net-reconnecting", "net-closed");
    this.netState.classList.add("net-" + status);
    this.netState.title = status[0].toUpperCase() + status.slice(1);
    if (status !== "open") this.netStat.textContent = status === "closed" ? "offline" : status;
  }

  updateScoreboard(rows: PlayerSnapshot[], myId: string) {
    const sorted = [...rows].sort((a, b) => (b.kills - b.deaths) - (a.kills - a.deaths) || b.kills - a.kills);
    let html = `<h4>Pilots (${sorted.length})</h4>`;
    for (const r of sorted.slice(0, 12)) {
      const me = r.id === myId ? " me" : "";
      html += `<div class="row${me}"><span>${escapeHtml(r.name)}</span><span>${r.kills}</span><span>${r.deaths}</span></div>`;
    }
    this.scoreboard.innerHTML = html;
  }

  pushKill(killer: string, victim: string) {
    const row = document.createElement("div");
    row.className = "kill";
    row.innerHTML = killer
      ? `<b>${escapeHtml(killer)}</b> ▸ ${escapeHtml(victim)}`
      : `${escapeHtml(victim)} crashed`;
    this.killfeed.appendChild(row);
    setTimeout(() => row.remove(), 4500);
  }
}

function escapeHtml(s: string) {
  return s.replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);
}
